import { useCallback, useEffect, useRef } from "react";
import config from "../../../config.js";
import { calculateBearing } from "../../../components/driverConsole/commonFunctions.js";
import {
  RECONNECT_INTERVAL,
  MAX_RECONNECT_ATTEMPTS,
  MOVEMENT_THRESHOLD,
  MIN_MOVEMENT_THRESHOLD,
  MAX_HISTORY_LENGTH,
} from "./mapConfig";

export const useVehicleTracking = ({
  vehicle,
  position,
  heading,
  lastMovedPosition,
  findNearestPointOnRoute,
  detectVehicleMovement,
  calculateDistance,
  directionsRef,
  map,
  setPosition,
  setHeading,
  setSpeed,
  setIsMoving,
  setLastMovedPosition,
  setStoppedDuration,
  setSnappedPosition,
  setPreviousPosition,
  setPositionHistory,
  setIsOnline,
  setIsReconnecting,
}) => {
  const isMountedRef = useRef(true);
  const eventSourceRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const positionHistoryRef = useRef({});

  const fetchLocation = useCallback(() => {
    if (!vehicle?.device_id || eventSourceRef.current) return;

    const connectSSE = () => {
      const query = `vehicleId=${vehicle.device_id}`;
      const sseUrl = `${config.baseURL}/redis/stream?${query}`;
      const eventSource = new EventSource(sseUrl);
      eventSourceRef.current = eventSource;

      eventSource.onmessage = (event) => {
        try {
          const locationData = JSON.parse(event.data);
          console.log("SSE Data Received:", locationData);
          if (!isMountedRef.current) return;

          const newPosition = {
            lat: parseFloat(locationData?.Latitude || locationData?.latitude),
            lng: parseFloat(locationData?.Longitude || locationData?.longitude),
            timestamp: Date.now(),
          };

          if (!newPosition.lat || !newPosition.lng) {
            console.error("Invalid position data:", newPosition);
            return;
          }

          setIsOnline(true);
          setIsReconnecting(false);
          reconnectAttemptsRef.current = 0;

          // Initialize position history for this vehicle
          if (!positionHistoryRef.current[vehicle.device_id]) {
            positionHistoryRef.current[vehicle.device_id] = [];
          }

          // Add new position to history
          positionHistoryRef.current[vehicle.device_id].push({
            lat: newPosition.lat,
            lng: newPosition.lng,
            timestamp: Date.now(),
          });

          // Keep only last N positions
          const history = positionHistoryRef.current[vehicle.device_id];
          if (history.length > MAX_HISTORY_LENGTH) {
            positionHistoryRef.current[vehicle.device_id] = history.slice(
              -MAX_HISTORY_LENGTH
            );
          }

          // Calculate heading from movement
          let calculatedHeading = heading;
          if (history.length >= 2) {
            const prevPos = history[history.length - 2];
            const currPos = history[history.length - 1];
            const distMoved = calculateDistance(prevPos, currPos);

            if (distMoved > MIN_MOVEMENT_THRESHOLD) {
              calculatedHeading = calculateBearing(
                { latitude: prevPos.lat, longitude: prevPos.lng },
                { latitude: currPos.lat, longitude: currPos.lng }
              );
            }
          }

          setPositionHistory((prev) => [...prev, newPosition].slice(-25));

          const speed =
            parseFloat(locationData?.speed || locationData?.Speed) || 0;
          setSpeed(speed);

          const moving = detectVehicleMovement(newPosition, lastMovedPosition);
          if (moving) {
            setIsMoving(true);
            setLastMovedPosition(newPosition);
            setStoppedDuration(0);
          } else {
            setIsMoving(false);
            setStoppedDuration((prev) => prev + 10);
          }

          if (directionsRef.current) {
            const snapped = findNearestPointOnRoute(
              newPosition,
              directionsRef.current
            );
            if (snapped) {
              setSnappedPosition(snapped);
              setPosition({
                ...newPosition,
                lat: snapped.lat,
                lng: snapped.lng,
              });
              setHeading(calculatedHeading);
            } else {
              // Update position even if off-route to keep vehicle marker moving
              setPosition(newPosition);
              setHeading(calculatedHeading);
            }
          } else {
            setPosition(newPosition);
            setHeading(calculatedHeading);
          }

          if (map) {
            map.panTo(newPosition);
          }
        } catch (error) {
          console.error("Error processing SSE data:", error);
        }
      };

      eventSource.onerror = () => {
        setIsOnline(false);
        setIsReconnecting(true);
        eventSource.close();
        eventSourceRef.current = null;
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current += 1;
          console.log(
            `Reconnecting SSE, attempt ${reconnectAttemptsRef.current}`
          );
          setTimeout(connectSSE, RECONNECT_INTERVAL);
        } else {
          console.error("Max reconnect attempts reached");
          setIsReconnecting(false);
        }
      };
    };

    connectSSE();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [
    vehicle?.device_id,
    findNearestPointOnRoute,
    detectVehicleMovement,
    calculateDistance,
    map,
    setPosition,
    setHeading,
    setSpeed,
    setIsMoving,
    setLastMovedPosition,
    setStoppedDuration,
    setSnappedPosition,
    setPositionHistory,
    setIsOnline,
    setIsReconnecting,
  ]);

  useEffect(() => {
    if (!vehicle?.device_id) return;
    isMountedRef.current = true;
    const cleanup = fetchLocation();
    return () => {
      isMountedRef.current = false;
      if (typeof cleanup === "function") cleanup();
    };
  }, [vehicle?.device_id, fetchLocation]);

  return { isMountedRef, eventSourceRef };
};
